/**
 * FilePreview - Componente per anteprima file
 */

import { useState, useEffect } from 'react';
import { X, Maximize2, Minimize2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { FileIcon, getMimeType } from './FileIcon';
import { FileSystemEntry } from '@/hooks/useFileSystem';

interface FilePreviewProps {
  entry: FileSystemEntry;
  blob: Blob;
  onClose: () => void;
}

export function FilePreview({ entry, blob, onClose }: FilePreviewProps) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const extension = entry.name.split('.').pop()?.toLowerCase() || '';
  const mimeType = getMimeType(entry.name);

  useEffect(() => {
    // Crea object URL per il blob
    const url = URL.createObjectURL(blob);
    setObjectUrl(url);

    // Per file di testo, leggi il contenuto
    if (isTextFile(extension)) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setTextContent(e.target?.result as string);
        setIsLoading(false);
      };
      reader.onerror = () => setIsLoading(false);
      reader.readAsText(blob);
    } else {
      setIsLoading(false);
    }

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [blob, extension]);

  const isTextFile = (ext: string) => {
    return ['txt', 'md', 'log', 'json', 'xml', 'html', 'css', 'js', 'ts', 'csv'].includes(ext);
  };

  const isImageFile = (ext: string) => {
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(ext);
  };

  const isPdfFile = (ext: string) => {
    return ext === 'pdf';
  };

  // Apri in nuova tab
  const handleOpenInNewTab = () => {
    if (objectUrl) {
      window.open(objectUrl, '_blank');
    }
  };

  const renderPreview = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      );
    }

    if (!objectUrl) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
          <FileIcon name={entry.name} kind="file" size={64} />
          <p className="mt-4">Impossibile visualizzare il file</p>
        </div>
      );
    }

    // PDF
    if (isPdfFile(extension)) {
      return (
        <iframe
          src={objectUrl}
          className="w-full h-full border-0"
          title={entry.name}
        />
      );
    }

    // Immagini
    if (isImageFile(extension)) {
      return (
        <div className="flex items-center justify-center h-full p-4 bg-checkered">
          <img
            src={objectUrl}
            alt={entry.name}
            className="max-w-full max-h-full object-contain"
          />
        </div>
      );
    }

    // Testo
    if (isTextFile(extension) && textContent !== null) {
      return (
        <div className="h-full overflow-auto bg-gray-900 text-gray-100 p-4">
          <pre className="text-sm font-mono whitespace-pre-wrap break-all">
            {textContent}
          </pre>
        </div>
      );
    }

    // File non supportato
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <FileIcon name={entry.name} kind="file" size={64} />
        <p className="mt-4 text-lg font-medium">{entry.name}</p>
        <p className="text-sm">Anteprima non disponibile per questo tipo di file</p>
      </div>
    );
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent
        className={cn(
          'max-w-4xl',
          isFullscreen && 'max-w-none w-screen h-screen m-0 rounded-none'
        )}
      >
        <DialogHeader className="flex flex-row items-center justify-between space-y-0 pb-2 border-b">
          <div className="flex items-center gap-2">
            <FileIcon name={entry.name} kind="file" size={20} />
            <DialogTitle className="text-base">{entry.name}</DialogTitle>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleOpenInNewTab}
              title="Apri in nuova scheda"
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsFullscreen(!isFullscreen)}
              title={isFullscreen ? 'Esci da schermo intero' : 'Schermo intero'}
            >
              {isFullscreen ? (
                <Minimize2 className="h-4 w-4" />
              ) : (
                <Maximize2 className="h-4 w-4" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              title="Chiudi"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>
        <div
          className={cn(
            'bg-muted/30 rounded overflow-hidden',
            isFullscreen ? 'h-[calc(100vh-80px)]' : 'h-[70vh]'
          )}
        >
          {renderPreview()}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default FilePreview;
